/* eslint-disable import/no-unused-modules */

import type { ColumnDef, SurchargeConfig } from './type/types';

/**
 * Right-hand metrics block (qty + money cols).
 *
 * Totals below use justify-between with terms `w-[45rem]` + summary `w-lg` (32rem).
 * When that row overflows, both shrink in proportion to those bases (45:32).
 * Mirror that used width here so QTY lines up with the totals labels without
 * changing the summary layout. Padding matches SummaryRow label/amount insets
 * (pl 0.2 + pl-2) and the previous PRICE column edge (pr 1.62).
 */
export const PRODUCT_TABLE_METRICS_CLASS =
    'flex w-[min(32rem,calc(100%*32/77))] shrink-0 pl-[0.7rem] pr-[1.62rem] md:pl-[0.7rem] md:pr-[1.62rem] print:pl-[0.7rem] print:pr-[1.62rem]';

/**
 * Desktop metric column % widths inside {@link PRODUCT_TABLE_METRICS_CLASS}.
 * With discount: 15 / 35 / 25 / 25. Without: 35 / 35 / 25.
 */
export const getProductTableMetricWidths = (
    showDiscountColumn: boolean,
    showTaxColumn: boolean,
): Record<'qty' | 'unitPrice' | 'discount' | 'tax' | 'price', number> => {
    if (showDiscountColumn && showTaxColumn) {
        return { qty: 15, unitPrice: 25, discount: 20, tax: 15, price: 25 };
    }
    if (showDiscountColumn) {
        return { qty: 15, unitPrice: 35, discount: 25, tax: 0, price: 25 };
    }
    if (showTaxColumn) {
        return { qty: 35, unitPrice: 25, discount: 0, tax: 15, price: 25 };
    }
    return { qty: 35, unitPrice: 35, discount: 0, tax: 0, price: 25 };
};

/**
 * All possible product table columns.
 * Widths for metric cols come from {@link getProductTableMetricWidths}; className is alignment only.
 */
export const PRODUCT_TABLE_COLUMNS = [
    { key: 'title', header: 'Products', className: 'min-w-0 flex-1' },
    { key: 'qty', header: 'QTY', className: 'text-left' },
    {
        key: 'unitPrice',
        header: 'UNIT PRICE',
        className: 'text-left',
    },
    {
        key: 'discount',
        header: 'DISCOUNT',
        className: 'text-left',
    },
    { key: 'tax', header: 'TAX', className: 'text-left' },
    {
        key: 'price',
        header: 'PRICE',
        className: 'text-right',
    },
] as const;

/** Columns that live inside {@link PRODUCT_TABLE_METRICS_CLASS}. */
export const PRODUCT_TABLE_METRIC_KEYS = [
    'qty',
    'unitPrice',
    'discount',
    'tax',
    'price',
] as const;

/**
 * Payment table columns (for both quote and invoice, some columns may be invisible for quote or invoice):
 * title (header: "Scheduled Payments")
 * status (header: "STATUS", value always "Unpaid")
 * due (header: "DUE", eg "20.08.2025" for NZ/AU, "08.20.2025" for US)
 * paid on (header: "PAID ON", value always "-")
 * amount (header: "" eg "$ 8,734.00")
 * Pay Button (header: "", button text "Pay" or "Paid")
 */

export const QUOTE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] md:pr-[1rem] print:pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 md:pl-0.25 print:pl-0.25',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6rem] md:pl-0 print:pl-0',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] md:pl-[0.4rem] print:pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[5.26rem] text-right md:pr-[0.63rem] print:pr-[0.63rem]',
    },
];

/** PDF-optimised quote columns: wider title, amount has room for full price */
export const PDF_QUOTE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 pl-0.25',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6.5rem]',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[8rem] text-right pr-[0.63rem]',
    },
];

export const INVOICE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] md:pr-[1rem] print:pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 md:pl-0.25 print:pl-0.25',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6rem] md:pl-0 print:pl-0',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] md:pl-[0.4rem] print:pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[5.26rem] text-right md:pr-[0.63rem] print:pr-[0.63rem]',
    },
    {
        key: 'payButton',
        header: '',
        className:
            'w-[11.2rem] flex justify-end md:pr-[1.3rem] print:pr-[1.3rem]',
    },
];

/** PDF-optimised invoice columns: wider title, amount has room for full price */
export const PDF_INVOICE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 pl-0.25',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6.5rem]',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[8rem] text-right pr-[0.63rem]',
    },
    {
        key: 'payButton',
        header: '',
        className: 'w-[11.2rem] flex justify-end pr-[1.3rem]',
    },
];

export const SURCHARGE_CONFIG_NZ: SurchargeConfig = {
    rate: 0.027,
    flatFee: 0,
};

export const SURCHARGE_CONFIG_AU: SurchargeConfig = {
    rate: 0.027,
    flatFee: 0,
};

export const SURCHARGE_CONFIG_US: SurchargeConfig = {
    rate: 0.027,
    flatFee: 0,
};

export const SURCHARGE_CONFIG_DEFAULT: SurchargeConfig = {
    rate: 0,
    flatFee: 0,
};
